import { FcCalendar, FcRight } from 'react-icons/fc';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    DeliveryState,
    useUpdateWarehouseDeliveriesStateMutation,
    WarehouseDeliveryFragmentFragment,
} from 'data/__generated__';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { formatDateAsAnniversary } from '../../../helpers/dateTimes';
import { Loader, LoaderModeType } from 'components/Loader';

export function MarkAsReadyPopup({
    warehouseDeliveries,
    selectedWarehouseDeliveryIds,
    setSelectedWarehouseDeliveryIds,
    isOpen,
    setIsOpen,
}: {
    warehouseDeliveries: WarehouseDeliveryFragmentFragment[];
    selectedWarehouseDeliveryIds: string[];
    setSelectedWarehouseDeliveryIds: (warehouseDeliveryIds: string[]) => void;
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
}) {
    const selectedWarehouseDeliveries = warehouseDeliveries.filter((warehouseDelivery) =>
        selectedWarehouseDeliveryIds.includes(warehouseDelivery._id),
    );

    const [updateWarehouseDeliveriesState, { loading }] = useUpdateWarehouseDeliveriesStateMutation({
        variables: { warehouseDeliveryIds: selectedWarehouseDeliveryIds, state: DeliveryState.Ready },
    });

    async function handleSubmit() {
        const { data } = await updateWarehouseDeliveriesState();
        if (!data) {
            throw new Error('Une erreur est survenue lors du passage des livraisons en prête(s)');
        }
        const {
            updateWarehouseDeliveriesState: { warehouseDeliveries, errors },
        } = data;

        if (warehouseDeliveries.length) {
            toast.success(`Livraison(s) d'entrepôt passée(s) en préparation : ${warehouseDeliveries.length}`);
        }
        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });
        }

        toast.success(`Livraison(s) d'entrepôt marquée(s) comme prête(s) : ${selectedWarehouseDeliveries.length}`);
        setSelectedWarehouseDeliveryIds([]);
        setIsOpen(false);
    }

    return (
        <TotemPopup title="Êtes-vous sûr.e ?" isOpen={isOpen} setIsOpen={setIsOpen}>
            Vous vous apprêtez à marquer {selectedWarehouseDeliveries.length} livraison(s) d'entrepôt comme prête(s).
            Souhaitez-vous vraiment marquer la/les livraison(s) d'entrepôt suivante(s) comme prête(s) ?
            <WarehouseDeliveryList>
                {selectedWarehouseDeliveries.map((warehouseDelivery) => {
                    const { _id: warehouseDeliveryId, deliveryDate, site } = warehouseDelivery;

                    return (
                        <WarehouseDelivery key={warehouseDeliveryId}>
                            <WarehouseDeliveryDate>
                                <FcCalendar size={26} />
                                {deliveryDate
                                    ? formatDateAsAnniversary({
                                          dateTime: deliveryDate,
                                          displayDay: true,
                                      })
                                    : 'Date non définie'}
                            </WarehouseDeliveryDate>
                            <WarehouseDeliverySite>
                                <FcRight size={26} />
                                {site?.name || warehouseDeliveryId}
                            </WarehouseDeliverySite>
                        </WarehouseDelivery>
                    );
                })}
            </WarehouseDeliveryList>
            <SubmitContainer>
                <TotemPrimaryButton onClick={handleSubmit}>
                    {loading ? <Loader size="20px" mode={LoaderModeType.Spin} /> : 'Confirmer'}
                </TotemPrimaryButton>
            </SubmitContainer>
        </TotemPopup>
    );
}

const WarehouseDeliveryList = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;
    padding-left: 50px;
    overflow: auto;

    & > :not(:first-child) {
        margin-top: 5px;
    }
`;

const WarehouseDelivery = styled.span`
    display: inline-flex;
    align-items: center;
`;

const WarehouseDeliveryDate = styled.span`
    display: inline-flex;
    align-items: center;
`;

const WarehouseDeliverySite = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 20px;
    font-weight: 800;
`;

const SubmitContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 15px;
`;
